import React, { useMemo } from 'react';
import { Empty, Space, Input, Switch, Button, Tooltip } from 'antd';
import {
    DeleteOutlined,
    SettingOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    PlusOutlined,
    ExclamationCircleFilled,
    LockOutlined,
} from '@ant-design/icons';
import { FormField_V2 } from '../../../common/typescript/models/CollectFormV2';
import { FieldTypeSelect } from './FieldTypeSelect';
import { validateFormFieldsDetailed, isFixedSystemFieldId } from '../hooks/useFormBuilder';

type FormCanvasProps = {
    fields: FormField_V2[];
    onFieldEdit: (field: FormField_V2, index?: number) => void;
    onFieldUpdate?: (fieldId: string, updatedField: FormField_V2) => void;
    onFieldUpdateAtIndex?: (index: number, updatedField: FormField_V2) => void;
    onFieldDelete: (fieldId: string, index?: number) => void;
    onFieldReorder?: (fromIndex: number, toIndex: number) => void;
    onQuickAddField?: () => void;
};

export const FormCanvas: React.FC<FormCanvasProps> = ({
    fields,
    onFieldEdit,
    onFieldUpdate,
    onFieldUpdateAtIndex,
    onFieldDelete,
    onFieldReorder,
    onQuickAddField,
}) => {
    const validationResult = useMemo(() => validateFormFieldsDetailed(fields), [fields]);
    const firstNameIdx = useMemo(
        () => fields.findIndex((f) => (f.id || '').trim().toLowerCase() === 'name'),
        [fields],
    );
    const firstDescIdx = useMemo(
        () => fields.findIndex((f) => (f.id || '').trim().toLowerCase() === 'description'),
        [fields],
    );

    const handleRowFieldChange = (index: number, oldField: FormField_V2, updatedField: FormField_V2) => {
        if (onFieldUpdateAtIndex) {
            onFieldUpdateAtIndex(index, updatedField);
        } else if (onFieldUpdate) {
            onFieldUpdate(oldField.id, updatedField);
        }
    };

    if (fields.length === 0) {
        return (
            <div
                style={{
                    height: '185px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    border: '1px dashed #cbd5e1',
                    borderRadius: '10px',
                    padding: '16px',
                }}
            >
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ margin: '0 0 8px 0' }}
                    description={
                        <Space direction="vertical" size={2} align="center">
                            <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                                Chưa có trường dữ liệu nào
                            </span>
                            <span style={{ fontSize: '12px', color: '#64748b' }}>
                                Nhấn &ldquo;Thêm trường nhanh&rdquo;, &ldquo;Thêm trường&rdquo; hoặc &ldquo;Chọn từ mẫu&rdquo; để bắt đầu
                            </span>
                        </Space>
                    }
                />
                {onQuickAddField && (
                    <Button
                        type="dashed"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={onQuickAddField}
                        style={{
                            borderRadius: 6,
                            color: '#2563eb',
                            borderColor: '#93c5fd',
                            background: '#ffffff',
                            fontWeight: 500,
                        }}
                    >
                        Thêm trường nhanh
                    </Button>
                )}
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div
                style={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    overflow: 'hidden',
                    background: '#ffffff',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                }}
            >
                <table
                    style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: 12,
                    }}
                >
                    <thead>
                        <tr
                            style={{
                                background: '#f8fafc',
                                borderBottom: '1px solid #e2e8f0',
                                color: '#475569',
                                fontWeight: 600,
                                textAlign: 'left',
                            }}
                        >
                            <th style={{ padding: '8px 10px', width: 64, textAlign: 'center' }}>TT</th>
                            <th style={{ padding: '8px 10px' }}>Tên trường hiển thị *</th>
                            <th style={{ padding: '8px 10px', width: 155 }}>Mã trường (ID) *</th>
                            <th style={{ padding: '8px 10px', width: 165 }}>Loại dữ liệu</th>
                            <th style={{ padding: '8px 10px', width: 78, textAlign: 'center' }}>Bắt buộc</th>
                            <th style={{ padding: '8px 10px', width: 78, textAlign: 'center' }}>Thao tác</th>
                        </tr>
                    </thead>
                    <tbody>
                        {fields.map((field, index) => {
                            const isFixedName = index === firstNameIdx;
                            const isFixedDesc = index === firstDescIdx;
                            const isFixed = isFixedName || isFixedDesc;
                            const isRequired = isFixedName ? true : Boolean(field.required);
                            const rowErr = validationResult.rowErrors.get(index);
                            const hasRowError = Boolean(rowErr?.labelError || rowErr?.idError);

                            return (
                                <tr
                                    key={index}
                                    style={{
                                        borderBottom:
                                            index < fields.length - 1 ? '1px solid #f1f5f9' : 'none',
                                        background: hasRowError ? '#fffafb' : isFixed ? '#fcfdff' : '#ffffff',
                                        verticalAlign: 'top',
                                    }}
                                >
                                    {/* 1. Order & Move Up/Down */}
                                    <td
                                        style={{
                                            padding: '8px 8px 6px 8px',
                                            textAlign: 'center',
                                            color: '#64748b',
                                            fontFamily:
                                                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 2,
                                            }}
                                        >
                                            <span style={{ minWidth: 16, fontWeight: 600, fontSize: 11 }}>
                                                {index + 1}
                                            </span>
                                            {onFieldReorder && (
                                                <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
                                                    <button
                                                        type="button"
                                                        disabled={index === 0}
                                                        onClick={() => onFieldReorder(index, index - 1)}
                                                        title="Di chuyển lên"
                                                        style={{
                                                            border: 'none',
                                                            background: 'transparent',
                                                            padding: 0,
                                                            lineHeight: 1,
                                                            cursor: index === 0 ? 'not-allowed' : 'pointer',
                                                            color: index === 0 ? '#cbd5e1' : '#64748b',
                                                            fontSize: 10,
                                                        }}
                                                    >
                                                        <ArrowUpOutlined />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={index === fields.length - 1}
                                                        onClick={() => onFieldReorder(index, index + 1)}
                                                        title="Di chuyển xuống"
                                                        style={{
                                                            border: 'none',
                                                            background: 'transparent',
                                                            padding: 0,
                                                            lineHeight: 1,
                                                            cursor:
                                                                index === fields.length - 1
                                                                    ? 'not-allowed'
                                                                    : 'pointer',
                                                            color:
                                                                index === fields.length - 1
                                                                    ? '#cbd5e1'
                                                                    : '#64748b',
                                                            fontSize: 10,
                                                        }}
                                                    >
                                                        <ArrowDownOutlined />
                                                    </button>
                                                </span>
                                            )}
                                        </div>
                                    </td>

                                    {/* 2. Field Label (Inline Editable + Validation) */}
                                    <td style={{ padding: '6px 8px', position: 'relative' }}>
                                        {/* Accessible text span for screen readers and text matchers */}
                                        <span
                                            style={{
                                                position: 'absolute',
                                                width: 1,
                                                height: 1,
                                                padding: 0,
                                                margin: -1,
                                                overflow: 'hidden',
                                                clip: 'rect(0, 0, 0, 0)',
                                                whiteSpace: 'nowrap',
                                                border: 0,
                                            }}
                                        >
                                            {field.label}
                                        </span>
                                        <Input
                                            size="small"
                                            status={rowErr?.labelError ? 'error' : undefined}
                                            value={field.label}
                                            placeholder="Nhập tên trường..."
                                            onChange={(e) =>
                                                handleRowFieldChange(index, field, {
                                                    ...field,
                                                    label: e.target.value,
                                                })
                                            }
                                            style={{
                                                borderRadius: 6,
                                                fontWeight: 500,
                                                color: '#1e293b',
                                            }}
                                        />
                                        {rowErr?.labelError && (
                                            <div
                                                style={{
                                                    fontSize: 10.5,
                                                    color: '#dc2626',
                                                    marginTop: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 4,
                                                }}
                                            >
                                                <ExclamationCircleFilled style={{ fontSize: 10 }} />
                                                <span>{rowErr.labelError}</span>
                                            </div>
                                        )}
                                    </td>

                                    {/* 3. Field ID / Key (Inline Editable Monospace + Duplicate/Format Validation) */}
                                    <td style={{ padding: '6px 8px' }}>
                                        {isFixed ? (
                                            <Tooltip title="Mã trường cố định của hệ thống (không thể thay đổi)">
                                                <div>
                                                    <Input
                                                        size="small"
                                                        disabled
                                                        value={field.id}
                                                        suffix={<LockOutlined style={{ color: '#94a3b8', fontSize: 11 }} />}
                                                        style={{
                                                            borderRadius: 6,
                                                            fontFamily:
                                                                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                                            fontSize: 11,
                                                            color: '#475569',
                                                            background: '#f1f5f9',
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                </div>
                                            </Tooltip>
                                        ) : (
                                            <Input
                                                size="small"
                                                status={rowErr?.idError ? 'error' : undefined}
                                                value={field.id}
                                                placeholder="vd: ten_chu_rung"
                                                onChange={(e) =>
                                                    handleRowFieldChange(index, field, {
                                                        ...field,
                                                        id: e.target.value.replace(/\s+/g, '_'),
                                                    })
                                                }
                                                style={{
                                                    borderRadius: 6,
                                                    fontFamily:
                                                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                                    fontSize: 11,
                                                    color: '#334155',
                                                    background: rowErr?.idError ? '#fff1f2' : '#f8fafc',
                                                }}
                                            />
                                        )}
                                        {rowErr?.idError && (
                                            <div
                                                style={{
                                                    fontSize: 10.5,
                                                    color: '#dc2626',
                                                    marginTop: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 4,
                                                }}
                                            >
                                                <ExclamationCircleFilled style={{ fontSize: 10 }} />
                                                <span>{rowErr.idError}</span>
                                            </div>
                                        )}
                                    </td>

                                    {/* 4. Field Type (Inline FieldTypeSelect with Icon) */}
                                    <td style={{ padding: '6px 8px' }}>
                                        <FieldTypeSelect
                                            size="small"
                                            value={field.type}
                                            onChange={(val) =>
                                                handleRowFieldChange(index, field, {
                                                    ...field,
                                                    type: val,
                                                })
                                            }
                                        />
                                    </td>

                                    {/* 5. Required Switch (Inline Toggle) */}
                                    <td style={{ padding: '8px 8px 6px 8px', textAlign: 'center' }}>
                                        <Tooltip
                                            title={
                                                isFixedName
                                                    ? 'Trường "name" luôn bắt buộc để định danh đối tượng thu thập'
                                                    : undefined
                                            }
                                        >
                                            <span>
                                                <Switch
                                                    size="small"
                                                    disabled={isFixedName}
                                                    checked={isRequired}
                                                    onChange={(checked) =>
                                                        handleRowFieldChange(index, field, {
                                                            ...field,
                                                            required: checked,
                                                        })
                                                    }
                                                />
                                            </span>
                                        </Tooltip>
                                    </td>

                                    {/* 6. Row Actions: Detailed Settings + Delete */}
                                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                        <Space size={2}>
                                            <Tooltip title="Cấu hình nâng cao (Tùy chọn, Ràng buộc, Gợi ý)">
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<SettingOutlined style={{ color: '#3b82f6' }} />}
                                                    onClick={() => onFieldEdit(field, index)}
                                                />
                                            </Tooltip>
                                            <Tooltip
                                                title={
                                                    isFixed
                                                        ? 'Trường mặc định cố định của biểu mẫu (không thể xóa)'
                                                        : 'Xóa trường'
                                                }
                                            >
                                                <span>
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        danger={!isFixed}
                                                        disabled={isFixed}
                                                        icon={<DeleteOutlined />}
                                                        onClick={() => onFieldDelete(field.id, index)}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </Space>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {onQuickAddField && (
                <div>
                    <Button
                        type="dashed"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={onQuickAddField}
                        style={{
                            width: '100%',
                            borderRadius: 6,
                            color: '#2563eb',
                            borderColor: '#bfdbfe',
                            background: '#f8fafc',
                            fontWeight: 500,
                            height: 30,
                        }}
                    >
                        Thêm trường nhanh
                    </Button>
                </div>
            )}
        </div>
    );
};
